import moment from "moment";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import AppInput from "../../components/Core/AppInput/AppInput";
import Flex from "../../components/Layout/Flex";
import TableLayout from "../../components/Layout/TableLayout";
import Breadcrumbs from "../../components/Shared/Breadcrumbs/Breadcrumbs";
import LoadingIndicator from "../../components/Shared/LoadingIndicator/LoadingIndicator";
import Pagination from "../../components/Shared/Pagination/Pagination";
import SortIcon from "../../components/Shared/SortIcon/SortIcon";
import Tag from "../../components/Shared/Tag/Tag";
import { InvoiceBreadcrumbs } from "../../configs/breadcrumbNavigation";
import { DATE_TIME_FORMAT_TYPES } from "../../configs/constants/DateTimeFormatTypes";
import { PAGE_SIZE } from "../../configs/constants/Pagination";
import { routes } from "../../configs/constants/routes";
import useDebounce from "../../hooks/useDebounce";
import { INVOICE_SORTING_COLUMN, SORT_ORDER } from "../../models/Sorting";
import { useInvoices } from "../../networking/invoice.hooks";
import { formatRoute } from "../../utils/route.utils";
import { formatCurrencyString } from "../../utils/string.utils";

function formatPrice(num: number) {
	return formatCurrencyString(num, { maximumFractionDigits: 2 });
}

const InvoiceListPage: React.FC = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();

	const [pageNumber, setPageNumber] = useState<number>(1);
	const [searchTerm, setSearchTerm] = useState<string>("");
	const keyword = useDebounce(searchTerm, 500);
	const [order, setOrder] = useState<SORT_ORDER>();
	const [sort, setSort] = useState<INVOICE_SORTING_COLUMN>();

	const { invoices, totalInvoicesCount, isLoading } = useInvoices({
		page: pageNumber,
		pageSize: PAGE_SIZE,
		keyword,
		sort,
		order,
	});

	const navigateToInvoiceDetails = (invoiceId?: string) => {
		navigate(formatRoute(routes.INVOICE_DETAILS, [invoiceId]));
	};

	const handleSortColumn = async (sort?: INVOICE_SORTING_COLUMN) => {
		handleSortingOrder();
		setSort(sort);
	};

	const handleSortingOrder = () => {
		if (!order) {
			setOrder(SORT_ORDER.ASC);
		} else if (order === SORT_ORDER.ASC) {
			setOrder(SORT_ORDER.DESC);
		} else if (order === SORT_ORDER.DESC) {
			setOrder(SORT_ORDER.ASC);
		}
	};

	return (
		<>
			<Breadcrumbs items={InvoiceBreadcrumbs} />
			<div className="p-4 px-6">
				<Flex justifyContent="space-between">
					<AppInput
						containerWidth="200px"
						placeholder={t("SEARCH")}
						icon="search"
						onChange={(e: any) => setSearchTerm(e.target.value)}
					/>
				</Flex>
				{isLoading ? (
					<LoadingIndicator />
				) : (
					<div>
						<TableLayout>
							<table>
								<thead>
									<tr>
										<th
											style={{ width: 130 }}
											onClick={() => handleSortColumn(INVOICE_SORTING_COLUMN.INVOICE_NO)}
										>
											{t("invoice.invoiceNumber")}
											<SortIcon
												sortType={
													sort === INVOICE_SORTING_COLUMN.INVOICE_NO ? order : SORT_ORDER.DEFAULT
												}
											/>
										</th>
										<th style={{ width: 130 }} />
										<th onClick={() => handleSortColumn(INVOICE_SORTING_COLUMN.INVOICE_DATE)}>
											{t("invoice.invoiceDate")}
											<SortIcon
												sortType={
													sort === INVOICE_SORTING_COLUMN.INVOICE_DATE ? order : SORT_ORDER.DEFAULT
												}
											/>
										</th>
										<th onClick={() => handleSortColumn(INVOICE_SORTING_COLUMN.CUSTOMER_NAME)}>
											{t("invoice.customer")}

											<SortIcon
												sortType={
													sort === INVOICE_SORTING_COLUMN.CUSTOMER_NAME ? order : SORT_ORDER.DEFAULT
												}
											/>
										</th>
										<th>{t("customers.customerNumberShort")}</th>
										<th onClick={() => handleSortColumn(INVOICE_SORTING_COLUMN.TOTAL)}>
											{t("invoice.total")}

											<SortIcon
												sortType={
													sort === INVOICE_SORTING_COLUMN.TOTAL ? order : SORT_ORDER.DEFAULT
												}
											/>
										</th>
									</tr>
								</thead>
								<tbody>
									{invoices.map(
										({
											id,
											invoiceNumber,
											invoiceDate,
											invoiceName,
											total,
											credit,
											creditInvoiceReference,
											customerNumber,
										}) => (
											<tr
												key={id}
												onClick={() => navigateToInvoiceDetails(id)}
												className={credit ? "warning" : ""}
											>
												<td>{invoiceNumber}</td>
												<td>
													{creditInvoiceReference && (
														<Tag size="s" color="danger" text={t("invoice.credited")}></Tag>
													)}
													{credit && (
														<Tag size="s" color="danger" text={t("invoice.credit.credit")}></Tag>
													)}
												</td>
												<td>{moment(invoiceDate).format(DATE_TIME_FORMAT_TYPES.DATE)}</td>
												<td>{invoiceName}</td>
												<td>{customerNumber}</td>
												<td>{formatPrice(total)}</td>
											</tr>
										)
									)}
								</tbody>
							</table>
						</TableLayout>
						{totalInvoicesCount > PAGE_SIZE && (
							<Pagination
								currentPage={pageNumber}
								totalCount={totalInvoicesCount}
								onClickItem={(e) => setPageNumber(e)}
							/>
						)}
					</div>
				)}
			</div>
		</>
	);
};

export default InvoiceListPage;
